import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import Page from 'components/layouts/page/Page';
import Button from 'components/shared/Button';
import CircularProgress from 'components/shared/CircularProgress';
import Toggle from 'components/shared/Toggle';
import { simpleDateAtTime } from 'lib/date';
import { adminClientsQueryKeys, adminShowClient } from 'services/api/admin/clients';
import {
  AdminCreateLiveRunParams,
  adminCreatePccRetroactiveDischargeRun,
  AdminCreatePreviewRunParams,
  adminIndexAllPccRetroactiveDischargeRunItems,
  adminIndexPccRetroactiveDischargeRuns,
  adminInvokePccRetroactiveDischargeRunAction,
  adminPccRetroactiveDischargeRunsQueryKeys as keys,
  adminSetPccRetroactiveDischargeRunItemsLeftForStaff,
  adminShowPccRetroactiveDischargeRun,
  isTerminalRun,
  RetroactiveDischargeRun,
  RetroactiveDischargeRunAction,
  RetroactiveDischargeRunItem,
} from 'services/api/admin/pccRetroactiveDischargeRuns';
import { validationErrorMessage } from 'services/api/validationErrors';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import { H2 } from 'styles/typography';

import ConfirmDischargeModal from './ConfirmDischargeModal';
import { hasPccAutoDischarge, liveSplit, previewSplit, runLabel, skipReasonLabel, waveProgress } from './labels';
import ReviewTable, { isSelectableRow, ReviewRow } from './ReviewTable';
import RunCounters, { Counter } from './RunCounters';
import RunHistory from './RunHistory';
import RunProgressBar from './RunProgressBar';

export const POLL_INTERVAL_MS = 3000;
const HISTORY_PER_PAGE = 100;

type Props = { pollIntervalMs?: number };

// Admin > Clients > (three dot menu) > Retroactive Discharges: find a PointClickCare client's stays
// that PCC discharged before auto discharge was on, try one, approve the rest and watch it finish.
export default function RetroactiveDischarges({ pollIntervalMs = POLL_INTERVAL_MS }: Props) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: client, isLoading } = useQuery({
    queryKey: adminClientsQueryKeys.show(id!),
    queryFn: ({ signal }) => adminShowClient(id!, signal),
    enabled: !!id,
  });

  if (isLoading || !client) return <CircularProgress />;

  return (
    <Page>
      <Page.Header>
        <HeaderRow>
          <Title>Retroactive Discharges: {client.name}</Title>
          <Button variant='ghost' onClick={() => navigate('/admin/clients')}>
            Back to Clients
          </Button>
        </HeaderRow>
      </Page.Header>
      <Page.Content>
        {hasPccAutoDischarge(client) ? (
          <RunsPanel clientId={client.id} clientName={client.name} pollIntervalMs={pollIntervalMs} />
        ) : (
          <Note>
            PCC auto discharge is not on for {client.name}. Turn it on in Configure API User before running retroactive
            discharges.
          </Note>
        )}
      </Page.Content>
    </Page>
  );
}

// Polls a run every few seconds until it settles, then tells the caller once so the lists refresh.
function usePolledRun(
  run: RetroactiveDischargeRun | undefined,
  intervalMs: number,
  onSettled: (runId: string) => void
) {
  const { data } = useQuery({
    queryKey: keys.show(run?.id ?? 'none'),
    queryFn: ({ signal }) => adminShowPccRetroactiveDischargeRun(run!.id, signal),
    enabled: !!run && !isTerminalRun(run),
    refetchInterval: (query) => (query.state.data && isTerminalRun(query.state.data) ? false : intervalMs),
  });
  // The history polls too, and once it has the run settled this query stops, so its last copy can be the older one
  const latest = data && run && data.id === run.id && data.updatedAt > run.updatedAt ? data : run;
  const settled = !!latest && isTerminalRun(latest);
  const wasActive = useRef(false);

  useEffect(() => {
    if (settled && wasActive.current) onSettled(latest.id);
    wasActive.current = !!latest && !settled;
  }, [latest, settled, onSettled]);

  return latest;
}

type PanelProps = { clientId: string; clientName: string; pollIntervalMs: number };

type LeftForStaffChange = { runId: string; itemIds: string[]; leftForStaff: boolean };

function RunsPanel({ clientId, clientName, pollIntervalMs }: PanelProps) {
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();
  const [includeUnflagged, setIncludeUnflagged] = useState(false);
  const [confirming, setConfirming] = useState<{ items: RetroactiveDischargeRunItem[]; bulk: boolean } | null>(null);

  // Another admin can start or settle a run from their own tab, so the list keeps up while anything is going
  // and catches up when this tab comes back into focus.
  const historyQuery = useQuery({
    queryKey: keys.index(clientId, { perPage: HISTORY_PER_PAGE }),
    queryFn: ({ signal }) =>
      adminIndexPccRetroactiveDischargeRuns(clientId, { page: 1, perPage: HISTORY_PER_PAGE }, signal),
    refetchInterval: (query) => (query.state.data?.data.some((run) => !isTerminalRun(run)) ? pollIntervalMs : false),
    refetchOnWindowFocus: true,
  });
  const runs = historyQuery.data?.data ?? [];

  const invalidateRuns = useCallback(() => queryClient.invalidateQueries({ queryKey: keys.lists }), [queryClient]);
  const onRunSettled = useCallback(
    (runId: string) => {
      invalidateRuns();
      queryClient.invalidateQueries({ queryKey: keys.items(runId) });
    },
    [queryClient, invalidateRuns]
  );

  // The newest preview is the review list; the live runs started from it are what happened to it.
  const latestPreview = runs.find((run) => run.mode === 'preview');
  const liveRunsFromHistory = latestPreview
    ? runs.filter((run) => run.mode === 'live' && run.sourceRunId === latestPreview.id)
    : [];
  const preview = usePolledRun(latestPreview, pollIntervalMs, onRunSettled);
  const currentLive = usePolledRun(liveRunsFromHistory[0], pollIntervalMs, onRunSettled);
  const liveRuns = liveRunsFromHistory.map((run) => (run.id === currentLive?.id ? currentLive : run));

  const previewReady = !!preview && preview.status === 'ready_for_review';
  const previewActive = !!preview && !isTerminalRun(preview);
  const liveActive = !!currentLive && !isTerminalRun(currentLive);
  const progressing = (previewActive && !preview.stalled) || (liveActive && !currentLive.stalled);

  const previewItemsQuery = useQuery({
    queryKey: keys.items(preview?.id ?? 'none'),
    queryFn: ({ signal }) => adminIndexAllPccRetroactiveDischargeRunItems(preview!.id, signal),
    enabled: previewReady,
  });

  // A "send one" is a run of its own, so there can be many small ones, and the table overlays all of them. Only
  // the run still going is polled; a settled run's items are fetched once, and again only when it settles.
  const liveItemsQueries = useQueries({
    queries: liveRuns.map((run) => ({
      queryKey: keys.items(run.id),
      queryFn: ({ signal }: { signal: AbortSignal }) => adminIndexAllPccRetroactiveDischargeRunItems(run.id, signal),
      staleTime: Infinity,
      refetchInterval: liveActive && run.id === currentLive.id ? pollIntervalMs : false,
    })),
  });
  // A failed poll keeps the items it had, so only a list that never arrived counts as not loaded
  const liveItemsLoaded = liveItemsQueries.every((query) => query.data !== undefined);
  const itemsLoaded = previewItemsQuery.data !== undefined && liveItemsLoaded;
  const failedItemQueries = [previewItemsQuery, ...liveItemsQueries].filter(
    (query) => query.isError && query.data === undefined
  );

  // The newest live run that took a stay wins
  const liveByStay = new Map<string, { item: RetroactiveDischargeRunItem; run: RetroactiveDischargeRun }>();
  for (let index = liveRuns.length - 1; index >= 0; index--) {
    (liveItemsQueries[index]?.data ?? []).forEach((item) =>
      liveByStay.set(item.locationEpisodeId, { item, run: liveRuns[index] })
    );
  }
  const rows: ReviewRow[] = (previewItemsQuery.data ?? []).map((item) => {
    const live = liveByStay.get(item.locationEpisodeId);
    return { item, live: live?.item, liveStopped: !!live && !live.item.outcome && needsRerun(live.run) };
  });
  const currentLiveItems = (currentLive && liveItemsQueries[0]?.data) ?? [];

  const selectableRows = itemsLoaded ? rows.filter(isSelectableRow) : [];
  const selectedItems = selectableRows.map((row) => row.item).filter((item) => !item.leftForStaff);
  const leftForStaffItems = selectableRows.map((row) => row.item).filter((item) => item.leftForStaff);
  const selectedIds = new Set(selectedItems.map((item) => item.id));
  const stoppedRuns = liveRuns.filter(needsRerun);

  const setLeftForStaff = useMutation({
    mutationFn: ({ runId, itemIds, leftForStaff }: LeftForStaffChange) =>
      adminSetPccRetroactiveDischargeRunItemsLeftForStaff(runId, { itemIds, leftForStaff }),
    onMutate: async ({ runId, itemIds, leftForStaff }: LeftForStaffChange) => {
      await queryClient.cancelQueries({ queryKey: keys.items(runId) });
      const previous = queryClient.getQueryData<RetroactiveDischargeRunItem[]>(keys.items(runId));
      const ids = new Set(itemIds);
      queryClient.setQueryData<RetroactiveDischargeRunItem[]>(keys.items(runId), (items) =>
        items?.map((item) => (ids.has(item.id) ? { ...item, leftForStaff } : item))
      );
      return { previous };
    },
    onError: (error, { runId }, context) => {
      if (context?.previous) queryClient.setQueryData(keys.items(runId), context.previous);
      addToast({ text: validationErrorMessage(error) ?? 'That choice could not be saved. Please try again.' });
    },
  });

  const toggleRow = (itemId: string) => {
    const item = selectableRows.find((row) => row.item.id === itemId)?.item;
    if (!preview || !item) return;
    setLeftForStaff.mutate({ runId: preview.id, itemIds: [itemId], leftForStaff: !item.leftForStaff });
  };
  const toggleAll = (checked: boolean) => {
    const itemIds = selectableRows.filter((row) => row.item.leftForStaff === checked).map((row) => row.item.id);
    if (!preview || !itemIds.length) return;
    setLeftForStaff.mutate({ runId: preview.id, itemIds, leftForStaff: !checked });
  };

  // Both mutations stay pending until the history they refresh has come back, so nothing can be started twice
  // off a list that does not yet show the run just started.
  const createRun = useMutation({
    mutationFn: (params: AdminCreatePreviewRunParams | AdminCreateLiveRunParams) =>
      adminCreatePccRetroactiveDischargeRun(clientId, params),
    onSuccess: async (run) => {
      queryClient.setQueryData(keys.show(run.id), run);
      await invalidateRuns();
      setConfirming(null);
    },
    onError: (error) => {
      addToast({ text: validationErrorMessage(error) ?? 'The run could not be started. Please try again.' });
    },
  });

  const invokeAction = useMutation({
    mutationFn: ({ runId, action }: { runId: string; action: RetroactiveDischargeRunAction }) =>
      adminInvokePccRetroactiveDischargeRunAction(runId, action),
    onSuccess: async (run) => {
      queryClient.setQueryData(keys.show(run.id), run);
      await invalidateRuns();
    },
    onError: (error) => {
      addToast({ text: validationErrorMessage(error) ?? 'That did not go through. Please try again.' });
    },
  });

  const actionPending = createRun.isPending || invokeAction.isPending || setLeftForStaff.isPending;
  const anyRunActive = runs.some((run) => !isTerminalRun(run));
  const busy = actionPending || previewActive || liveActive || anyRunActive;
  const startingPreview = createRun.isPending && createRun.variables?.mode === 'preview';
  const rerun = (runId: string) => invokeAction.mutate({ runId, action: 'rerun' });

  const startLive = () => {
    if (!preview || !confirming) return;
    createRun.mutate({ mode: 'live', sourceRunId: preview.id, itemIds: confirming.items.map((item) => item.id) });
  };

  return (
    <Stack>
      <Card aria-label='Retroactive discharges'>
        <CardHead>
          <div>
            <H2>Retroactive discharges</H2>
            <Description>
              Finds stays still In Treatment in Olio that carry the “ready to discharge” indicator from a PointClickCare
              discharge that arrived before auto discharge was turned on, and discharges them with the same rules as a
              live auto discharge. No emails are sent.
            </Description>
          </div>
          <Button
            onClick={() => createRun.mutate({ mode: 'preview', includeUnflagged })}
            disabled={busy}
            loading={startingPreview}
            data-testid='find-patients'>
            Find patients (preview)
          </Button>
        </CardHead>

        <SettingRow>
          <Toggle active={includeUnflagged} disabled={busy} onChange={setIncludeUnflagged} />
          <div>
            <SettingLabel>Include stays without the “ready to discharge” indicator</SettingLabel>
            <SettingHelp>
              Off by default. Checks every In Treatment PointClickCare stay, which is slower, to catch a discharge Olio
              never received.
            </SettingHelp>
          </div>
        </SettingRow>

        {!preview && (
          <Note>
            Nothing changes until you choose which patients to discharge. You can send one patient first to check the
            result.
          </Note>
        )}

        {preview?.status === 'finding_patients' && (
          <>
            <RunProgressBar
              label={`Checking ${preview.counts.total} stays · ${preview.counts.processed} checked`}
              value={preview.counts.processed}
              max={preview.counts.total}
            />
            <RunCounters counters={previewCounters(preview)} />
          </>
        )}

        {preview && (preview.status === 'failed' || (preview.status === 'finding_patients' && preview.stalled)) && (
          <ErrorBox>
            <span>
              {preview.status === 'failed'
                ? `The preview stopped: ${preview.error ?? 'something went wrong'}.`
                : 'The preview has stopped making progress.'}
            </span>
            <Button
              variant='ghost'
              onClick={() => rerun(preview.id)}
              disabled={actionPending || (liveActive && !currentLive.stalled)}
              data-testid='rerun-preview'>
              Try again
            </Button>
          </ErrorBox>
        )}

        {previewReady && (
          <>
            {!currentLive && (
              <>
                <RunCounters counters={previewCounters(preview)} />
                <Legend>
                  {preview.counts.wouldDischarge} patients will be discharged,{' '}
                  {preview.counts.wouldDischargeNeedsReview} will be discharged and flagged for review,{' '}
                  {preview.counts.skipped} will be skipped. Patients Olio will discharge are checked; uncheck any you
                  want to leave for staff.
                </Legend>
              </>
            )}

            {currentLive && liveActive && (
              <>
                <RunProgressBar
                  label={liveProgressLabel(currentLive)}
                  value={currentLive.counts.processed}
                  max={currentLive.counts.total}
                />
                <RunCounters counters={liveCounters(currentLive)} />
              </>
            )}

            {currentLive?.status === 'done' && itemsLoaded && (
              <>
                <SuccessBox data-testid='run-summary'>{liveSummary(currentLive, currentLiveItems, rows)}</SuccessBox>
                <RunCounters counters={liveCounters(currentLive)} />
                {listOutcomes(rows).skipped > 0 && (
                  <Legend data-testid='skipped-by-reason'>Skipped by reason: {skippedByReason(rows)}.</Legend>
                )}
              </>
            )}

            {stoppedRuns.map((run) => (
              <ErrorBox key={run.id} data-testid={`stopped-run-${run.id}`}>
                <span>{stoppedRunMessage(run)}</span>
                <Button
                  variant='ghost'
                  onClick={() => rerun(run.id)}
                  disabled={actionPending || (progressing && run.id !== currentLive?.id)}
                  data-testid={`rerun-live-${run.id}`}>
                  Re-run the rest
                </Button>
              </ErrorBox>
            ))}

            {failedItemQueries.length > 0 ? (
              <ErrorBox data-testid='items-load-failed'>
                <span>The patient list could not be loaded.</span>
                <Button
                  variant='ghost'
                  onClick={() => failedItemQueries.forEach((query) => query.refetch())}
                  data-testid='retry-items'>
                  Try again
                </Button>
              </ErrorBox>
            ) : !itemsLoaded ? (
              <CircularProgress />
            ) : (
              <ReviewTable
                rows={rows}
                selectable={!liveActive}
                selectedIds={selectedIds}
                onToggle={toggleRow}
                onToggleAll={toggleAll}
                onSendOne={(item) => setConfirming({ items: [item], bulk: false })}
                busy={busy}
              />
            )}

            <Actions>
              {liveActive && currentLive.status === 'running' && (
                <Button
                  variant='outline-gray'
                  onClick={() => invokeAction.mutate({ runId: currentLive.id, action: 'pause' })}
                  disabled={invokeAction.isPending}
                  data-testid='pause-run'>
                  Pause after this wave
                </Button>
              )}
              {liveActive && currentLive.status === 'paused' && (
                <Button
                  onClick={() => invokeAction.mutate({ runId: currentLive.id, action: 'resume' })}
                  disabled={invokeAction.isPending}
                  data-testid='resume-run'>
                  Resume
                </Button>
              )}
              {liveActive && (
                <Legend>
                  {currentLive.status === 'paused'
                    ? 'Paused. Nothing more is sent until you resume; patients already sent still finish.'
                    : 'Pausing lets the current wave finish and sends nothing more until you resume.'}
                </Legend>
              )}
              {!liveActive && (
                <>
                  <Button
                    onClick={() => setConfirming({ items: selectedItems, bulk: true })}
                    disabled={busy || !itemsLoaded || !selectedItems.length}
                    data-testid='discharge-selected'>
                    {itemsLoaded
                      ? `Discharge ${selectedItems.length} selected ${selectedItems.length === 1 ? 'patient' : 'patients'}`
                      : 'Discharge selected patients'}
                  </Button>
                  {currentLive?.status === 'done' && currentLive.counts.noResponse > 0 && (
                    <Button
                      variant='ghost'
                      onClick={() => rerun(currentLive.id)}
                      disabled={busy}
                      data-testid='retry-unresolved'>
                      Retry {currentLive.counts.noResponse} unresolved
                    </Button>
                  )}
                  <Legend>Use “Send one” on a row to test a single patient first.</Legend>
                </>
              )}
            </Actions>
          </>
        )}
      </Card>

      <Card aria-label='Past runs'>
        <H2>Past runs</H2>
        {historyQuery.isLoading ? (
          <CircularProgress />
        ) : (
          <RunHistory runs={runs} totalRecords={historyQuery.data?.meta.totalRecords ?? runs.length} />
        )}
      </Card>

      {confirming && preview && (
        <ConfirmDischargeModal
          clientName={clientName}
          previewTakenAt={preview.completedAt ?? preview.createdAt}
          pageSize={preview.pageSize}
          items={confirming.items}
          leftForStaff={confirming.bulk ? leftForStaffItems : []}
          isPending={createRun.isPending}
          onConfirm={startLive}
          onClose={() => setConfirming(null)}
        />
      )}
    </Stack>
  );
}

// A live run that will not finish its unsent patients on its own
function needsRerun(run: RetroactiveDischargeRun) {
  return (
    run.status === 'failed' || (!isTerminalRun(run) && run.stalled) || (run.status === 'done' && unsentCount(run) > 0)
  );
}

function unsentCount(run: RetroactiveDischargeRun) {
  return run.counts.toSend + run.counts.sent;
}

function stoppedRunMessage(run: RetroactiveDischargeRun) {
  const started = `${runLabel(run)} (started ${simpleDateAtTime(run.createdAt)})`;
  const left = unsentCount(run);
  const rest = left ? ` ${left} not finished.` : '';
  if (run.status === 'failed') return `${started} stopped: ${run.error ?? 'something went wrong'}.${rest}`;
  if (run.stalled) return `${started} has stopped making progress.${rest}`;
  return `${started} finished with patients never sent.${rest}`;
}

function previewCounters(run: RetroactiveDischargeRun): Counter[] {
  const { wave, totalWaves } = waveProgress(run);
  const finished = run.completedAt ? simpleDateAtTime(run.completedAt) : null;

  return [
    { label: 'To send', value: run.counts.toSend, sub: 'not yet checked' },
    { label: 'Sent', value: run.counts.sent, sub: 'waiting on an answer' },
    { label: 'Processed', value: run.counts.processed, sub: previewSplit(run.counts) },
    run.status === 'ready_for_review'
      ? { label: 'Run', value: 'Preview', sub: finished ? `finished ${finished} · nothing changed` : 'nothing changed' }
      : { label: 'Wave', value: `${wave} of ${totalWaves}`, sub: `${run.pageSize} stays per wave` },
  ];
}

function liveCounters(run: RetroactiveDischargeRun): Counter[] {
  const { wave, totalWaves } = waveProgress(run);
  const finished = run.completedAt ? simpleDateAtTime(run.completedAt) : null;
  const waveSub =
    run.status === 'done'
      ? finished
        ? `finished ${finished}`
        : 'finished'
      : run.status === 'paused'
        ? 'paused'
        : 'waits for every patient before the next';

  return [
    { label: 'To send', value: run.counts.toSend, sub: 'approved, not yet sent' },
    { label: 'Sent', value: run.counts.sent, sub: 'waiting on an outcome' },
    { label: 'Processed', value: run.counts.processed, sub: liveSplit(run.counts) },
    { label: 'Wave', value: `${wave} of ${totalWaves}`, sub: waveSub },
  ];
}

function liveProgressLabel(run: RetroactiveDischargeRun) {
  const { wave, totalWaves } = waveProgress(run);
  const noun = run.counts.total === 1 ? 'patient' : 'patients';
  const state = run.status === 'paused' ? 'paused' : 'in progress';

  return `Discharging ${run.counts.total} ${noun} · wave ${wave} of ${totalWaves} ${state}`;
}

// Where each stay on the preview ended up: what the newest live run did with it, else what the preview found.
// A stay the preview would discharge that no live run has taken is still left for staff.
function rowOutcome(row: ReviewRow) {
  const live = row.live?.outcome ? row.live : undefined;
  const source = live ?? row.item;
  if (source.outcome === 'discharged') return { kind: 'discharged' as const };
  if (source.outcome === 'skipped') return { kind: 'skipped' as const, reason: source.reason ?? '' };
  if (source.outcome === 'no_response') return { kind: 'noResponse' as const };
  return { kind: 'leftForStaff' as const };
}

function listOutcomes(rows: ReviewRow[]) {
  const totals = { discharged: 0, skipped: 0, noResponse: 0, leftForStaff: 0 };
  rows.forEach((row) => (totals[rowOutcome(row).kind] += 1));
  return totals;
}

// The summary covers the whole list (the preview and every run started from it), so skips the preview found and
// patients sent one at a time earlier count alongside this run's.
function liveSummary(run: RetroactiveDischargeRun, items: RetroactiveDischargeRunItem[], rows: ReviewRow[]) {
  const only = run.counts.total === 1 ? items[0] : undefined;

  if (only?.outcome === 'discharged') {
    return `${only.patient.name} was discharged. Open the patient to check the Patient Story before discharging the rest.`;
  }

  const { discharged, skipped, noResponse, leftForStaff } = listOutcomes(rows);
  const parts = [
    `${discharged} ${discharged === 1 ? 'patient' : 'patients'} discharged`,
    `${skipped} skipped`,
    ...(noResponse ? [`${noResponse} with no response`] : []),
    ...(leftForStaff ? [`${leftForStaff} left for staff`] : []),
  ];

  return `Run finished. For this list of ${rows.length}: ${parts.join(', ')}. Nothing was emailed.`;
}

function skippedByReason(rows: ReviewRow[]) {
  const counts = new Map<string, number>();
  rows.forEach((row) => {
    const outcome = rowOutcome(row);
    if (outcome.kind === 'skipped') counts.set(outcome.reason, (counts.get(outcome.reason) ?? 0) + 1);
  });

  return Array.from(counts, ([reason, count]) => `${skipReasonLabel(reason).toLowerCase()} ${count}`).join(' · ');
}

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 500;
  margin: 0;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Card = styled.section`
  background: ${colors.white};
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CardHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const Description = styled.p`
  font-size: 14px;
  line-height: 22px;
  color: ${colors.black75};
  margin: 4px 0 0;
  max-width: 72ch;
`;

const Note = styled.p`
  font-size: 14px;
  line-height: 22px;
  color: ${colors.black75};
  margin: 0;
`;

const SettingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  border-top: 1px solid ${colors.black10};
`;

const SettingLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

const SettingHelp = styled.div`
  font-size: 12px;
  line-height: 20px;
  color: ${colors.black75};
`;

const Legend = styled.p`
  font-size: 12px;
  line-height: 20px;
  color: ${colors.black75};
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

const SuccessBox = styled.div`
  background: ${colors.green5};
  border: 1px solid ${colors.green25};
  border-radius: 4px;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 22px;
`;

const ErrorBox = styled.div`
  background: ${colors.red5};
  border: 1px solid ${colors.red25};
  border-radius: 4px;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 22px;
  color: ${colors.red100};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;
